import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { PublicQuestion } from "@aletheia/shared";
import { quizApi, type AnswerResponse, type MistakeEntry, type ScoreResult } from "../../api/quiz";
import { ApiError } from "../../api/client";
import { quizStorage } from "./quizStorage";
import { hapticNotification } from "../../telegram/webApp";

export type QuizPhase = "idle" | "loading" | "question" | "submitting" | "feedback" | "completed" | "error";

export interface SelectedAnswer extends AnswerResponse {
  selectedOptionId: string;
}

interface QuizState {
  phase: QuizPhase;
  sessionId: string | null;
  total: number;
  question: PublicQuestion | null;
  lastAnswer: SelectedAnswer | null;
  scoreResult: ScoreResult | null;
  mistakes: MistakeEntry[] | null;
  errorMessage: string | null;
}

interface QuizContextValue extends QuizState {
  startNewTest: () => Promise<boolean>;
  submitAnswer: (optionId: string) => Promise<void>;
  advance: () => Promise<void>;
  loadMistakes: () => Promise<void>;
  reset: () => void;
}

const initialState: QuizState = {
  phase: "idle",
  sessionId: null,
  total: 0,
  question: null,
  lastAnswer: null,
  scoreResult: null,
  mistakes: null,
  errorMessage: null,
};

const QuizContext = createContext<QuizContextValue | null>(null);

export function QuizProvider({ children }: { children: ReactNode }) {
  // Start at "loading", not "idle", whenever a resumable session id is
  // already in storage — otherwise every screen that treats "idle" as "no
  // quiz, redirect away" (see QuizScreen) would briefly see "idle" on the
  // very first render even when a session is about to be resumed, on a
  // direct/refreshed load of a mid-quiz route.
  const [state, setState] = useState<QuizState>(() =>
    quizStorage.getSessionId() ? { ...initialState, phase: "loading" } : initialState,
  );
  const resumedRef = useRef(false);
  const startInFlightRef = useRef(false);

  const resume = useCallback(async () => {
    const existingId = quizStorage.getSessionId();
    if (!existingId) return;
    setState((s) => ({ ...s, phase: "loading" }));
    try {
      const res = await quizApi.getSession(existingId);
      if (res.completed) {
        quizStorage.clear();
        setState(initialState);
        return;
      }
      setState((s) => ({
        ...s,
        phase: "question",
        sessionId: existingId,
        total: res.total,
        question: res.question ?? null,
      }));
    } catch {
      quizStorage.clear();
      setState(initialState);
    }
  }, []);

  useEffect(() => {
    if (resumedRef.current) return;
    resumedRef.current = true;
    void resume();
  }, [resume]);

  const startNewTest = useCallback(async (): Promise<boolean> => {
    // Guards against a duplicate session being created by a double tap/click
    // landing before the "loading" phase has re-rendered and disabled the
    // button — a real race the UI's disabled-while-loading state alone
    // doesn't fully close.
    if (startInFlightRef.current) return false;
    startInFlightRef.current = true;
    setState((s) => ({ ...s, phase: "loading", errorMessage: null }));
    try {
      const res = await quizApi.createSession();
      quizStorage.setSessionId(res.sessionId);
      setState({
        ...initialState,
        phase: "question",
        sessionId: res.sessionId,
        total: res.total,
        question: res.question,
      });
      return true;
    } catch (e) {
      setState((s) => ({ ...s, phase: "error", errorMessage: describeError(e) }));
      // Caller (WelcomeScreen) must not navigate to /quiz on this false —
      // doing so anyway was a real bug: QuizScreen shows a bare loading
      // spinner for any phase other than "question", so a failed
      // createSession() call used to strand the user on an unexplained,
      // unrecoverable spinner instead of the error+retry UI that's still
      // right here on Welcome.
      return false;
    } finally {
      startInFlightRef.current = false;
    }
  }, []);

  const submitAnswer = useCallback(
    async (optionId: string) => {
      if (!state.sessionId || !state.question) return;
      setState((s) => ({ ...s, phase: "submitting" }));
      try {
        const res = await quizApi.submitAnswer(state.sessionId, state.question.id, optionId);
        hapticNotification(res.correct ? "success" : "warning");
        setState((s) => ({ ...s, phase: "feedback", lastAnswer: { ...res, selectedOptionId: optionId } }));
      } catch (e) {
        setState((s) => ({ ...s, phase: "error", errorMessage: describeError(e) }));
      }
    },
    [state.sessionId, state.question],
  );

  const advance = useCallback(async () => {
    if (!state.sessionId || !state.lastAnswer) return;

    if (state.lastAnswer.isLastQuestion) {
      setState((s) => ({ ...s, phase: "loading" }));
      try {
        const result = await quizApi.complete(state.sessionId);
        quizStorage.clear();
        setState((s) => ({ ...s, phase: "completed", scoreResult: result }));
      } catch (e) {
        setState((s) => ({ ...s, phase: "error", errorMessage: describeError(e) }));
      }
      return;
    }

    setState((s) => ({ ...s, phase: "loading" }));
    try {
      const res = await quizApi.getSession(state.sessionId);
      setState((s) => ({
        ...s,
        phase: "question",
        question: res.question ?? null,
        lastAnswer: null,
      }));
    } catch (e) {
      setState((s) => ({ ...s, phase: "error", errorMessage: describeError(e) }));
    }
  }, [state.sessionId, state.lastAnswer]);

  const loadMistakes = useCallback(async () => {
    if (!state.sessionId) return;
    try {
      const res = await quizApi.mistakes(state.sessionId);
      setState((s) => ({ ...s, mistakes: res.mistakes }));
    } catch (e) {
      setState((s) => ({ ...s, phase: "error", errorMessage: describeError(e) }));
    }
  }, [state.sessionId]);

  const reset = useCallback(() => {
    quizStorage.clear();
    setState(initialState);
  }, []);

  return (
    <QuizContext.Provider value={{ ...state, startNewTest, submitAnswer, advance, loadMistakes, reset }}>
      {children}
    </QuizContext.Provider>
  );
}

function describeError(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  return "Что-то пошло не так. Попробуйте ещё раз.";
}

export function useQuiz(): QuizContextValue {
  const ctx = useContext(QuizContext);
  if (!ctx) throw new Error("useQuiz must be used within QuizProvider");
  return ctx;
}
